import axios from "axios";
import { IAnimalPDTO } from "../models/dtos/animalPDTO";
import { IBoarPDTO } from "../models/dtos/boarPDTO";
import { IAnimalApiResponse } from "../models/api/animalApiResponse";
import { IBoarApiResponse } from "../models/api/boarApiResponse";
import { API_CENTRE } from "./config";
import { apiCallWithAuth } from "../helper/apiManager";
import { IFilterAnimal } from "../models/filterAnimal";

export default class AnimalService {

  /**
     * Récupération des informations de l'animal en fonction de son ID
     * @param id ID de l'animal à récupérer
     * @returns Animal reçu de l'API (avec IDs)
     */
  static async getAnimalById(id: number): Promise<IAnimalApiResponse | null> {
    try {
      const { data } = await apiCallWithAuth(
        API_CENTRE + "/api/v1/animals/" + id,
      );
      return data;
    } catch (err) {
      return null;
    }
  }

  /**
     * Récupération des informations d'un verrat
     * @param id ID du verrat à récupérer
     * @returns Verrat reçu de l'API (avec IDs)
     */
  static async getBoarById(id: number): Promise<IBoarApiResponse | null> {
    try {
      const { data } = await apiCallWithAuth(
        API_CENTRE + "/api/v1/boars/" + id,
        "get",
      );
      return data;
    } catch (err) {
      return null;
    }
  }

  /**
     * Récupération d'une liste d'animaux en fonction de différents filtres
     * @param filter Filtre de recherche
     * @returns Liste des animaux reçus de l'API (avec IDs)
     */
  static async getAnimals(filter: IFilterAnimal): Promise<{ error: boolean; data: IAnimalApiResponse[] }> {
    var url = this.getUrlFilter(filter, `/api/v1/animals/search?`);
    try {
      const { data } = await apiCallWithAuth(
        API_CENTRE + url,
        "get",
      );

      return {
        error: false,
        data: data,
      };
    } catch (err) {
      return {
        error: true,
        data: [],
      };
    }
  }

  static getUrlFilter(filter:IFilterAnimal,url:string)
  {
    url += "Include=*&AdditionalDataTab=Reforms&AdditionalDataTab=Caryotype";
    url += `&PageNumber=${filter.page}&PageSize=${filter.pageSize}`;
    if ( filter.idCenter != null)
      url += `&IdCenter=${filter.idCenter}`;
    if ( filter.isReform != null)
      url += `&IsReform=${filter.isReform}`;
    if ( filter.isUsual != null)
      url += `&IsUsual=${filter.isUsual}`;
    if ( filter.isPresent != null)
      url += `&IsPresent=${filter.isPresent}`;
    if ( filter.isRecentReform != null)
      url += `&IsRecentReform=${filter.isRecentReform}`;
    if ( filter.search != undefined)
      url += `&GlobalSearch=${filter.search}`;
    if ( filter.state != null)
      url += `&State=${filter.state}`;
    if ( filter.psLotId != null)
      url += `&PsLotId=${filter.psLotId}`;

    return url;
  }

  /**
     * Récupération d'une liste de verrats en fonction de différents filtres
     * @param filter Filtre de recherche
     * @returns Liste des verrats reçus de l'API (avec IDs)
     */
  static async getBoars(filter: IFilterAnimal): Promise<{ error: boolean; data: IBoarApiResponse[] }> {
    var url = this.getUrlFilter(filter, `/api/v1/boars/search?`);

    try {
      let { data } = await apiCallWithAuth(
        API_CENTRE + url,
        "get",
      );

      return {
        error: false,
        data: data,
      };
    } catch (err) {
      return {
        error: true,
        data: [],
      };
    }
  }

  /**
     * Création d'un animal
     * @param animal DTO de l'animal à créer
     * @returns Animal créé reçu de l'API (avec IDs)
     */
  static async createAnimal(animal: IAnimalPDTO): Promise<{ err: boolean; data: IAnimalApiResponse | unknown }> {
    try {
      const { data } = await apiCallWithAuth(
        API_CENTRE + "/api/v1/animals",
        "post",
        animal,
      );

      return {
        err: false,
        data: data,
      };
    } catch (err) {
      return {
        err: true,
        data: err,
      };
    }
  }

  /**
     * Création d'un verrat
     * @param boar DTO du verrat à créer
     * @returns Verrat créé reçu de l'API (avec IDs)
     */
  static async createBoar(boar: IBoarPDTO): Promise<{ err: boolean; data: IBoarApiResponse | unknown }> {
    try {
      const { data } = await apiCallWithAuth(
        API_CENTRE + "/api/v1/boars",
        "post",
        boar,
      );
      return {
        err: false,
        data: data,
      };
    } catch (err) {
      return {
        err: true,
        data: err,
      };
    }
  }

  /**
     * Mise à jour d'un animal
     * @param animal DTO de l'animal à modifier
     * @returns Animal mis à jour reçu de l'API (avec IDs)
     */
  static async updateAnimal(animal: IAnimalPDTO): Promise<{ err: boolean; data: IAnimalApiResponse | unknown }> {
    try {
      const { data } = await apiCallWithAuth(
        API_CENTRE + "/api/v1/animals?idAnimal=" + animal.id,
        "put",
        animal
      );
      return { err: false, data: data };
    } catch (err) {
      return {
        err: true,
        data: err,
      };
    }
  }

  /**
     * Mise à jour d'un verrat
     * @param boar DTO du verrat à modifier
     * @returns Verrat mis à jour reçu de l'API (avec IDs)
     */
  static async updateBoar(boar: IBoarPDTO): Promise<{ err: boolean; data: IBoarApiResponse | unknown }> {
    try {
      const { data } = await apiCallWithAuth(
        API_CENTRE + "/api/v1/boars?idAnimal=" + boar.id,
        "put",
        boar,
      );

      return { err: false, data: data };
    } catch (err) {
      return {
        err: true,
        data: err,
      };
    }
  }

  static async getLastQuarantineCycle( idCenter: number) {

    try {

      const { data } = await apiCallWithAuth(API_CENTRE + `/api/v1/animals/lastquarantine?idCenterAnimal=${idCenter}`, "get");

      return {
        error: false,
        data: data,
      };

    } catch (err) {
      return {
        error: true,
        data: err,
      };
    }

  }

}
